/**
 * Authentication Hook
 *
 * Manages JWT tokens for authenticated API requests.
 * Caches tokens per wallet address in memory with automatic expiry handling.
 */

import { useCallback, useRef } from 'react';
import { useAccount, useSignMessage } from 'wagmi';
import { useWallet } from '@solana/wallet-adapter-react';
import { requestAuthNonce, authLogin } from '../services/api';
import bs58 from 'bs58';

const TOKEN_CACHE_PREFIX = 'sw4p:auth:';
const TOKEN_EXPIRY_BUFFER_MS = 60_000; // Refresh 1 minute before expiry

interface CachedToken {
    token: string;
    expires_at: number;
    chain: string;
}

// Module-scoped in-memory token cache (not accessible to XSS via localStorage)
const tokenCache = new Map<string, CachedToken>();

export function useAuthToken() {
    const { address: evmAddress } = useAccount();
    const { signMessageAsync: evmSignMessage } = useSignMessage();
    const { publicKey: solPublicKey, signMessage: solSignMessage } = useWallet();
    const pendingRequests = useRef<Map<string, Promise<string>>>(new Map());

    /**
     * Get or create an auth token for the given wallet
     */
    const getAuthToken = useCallback(
        async ({ address, chain }: { address: string; chain: 'EVM' | 'SOL' }): Promise<string> => {
            const cacheKey = `${TOKEN_CACHE_PREFIX}${chain}:${address.toLowerCase()}`;

            // Check cache first
            const cached = tokenCache.get(cacheKey);
            if (cached && Date.now() < cached.expires_at - TOKEN_EXPIRY_BUFFER_MS) {
                return cached.token;
            }

            // Dedupe concurrent requests
            const existingRequest = pendingRequests.current.get(cacheKey);
            if (existingRequest) {
                return existingRequest;
            }

            const request = (async () => {
                try {
                    // 1. Get nonce
                    const { nonce } = await requestAuthNonce(address, chain);

                    // 2. Sign message
                    const message = `Sign this message to authenticate with SW4P.\n\nNonce: ${nonce}`;
                    let signature: string;

                    if (chain === 'EVM') {
                        signature = await evmSignMessage({ message });
                    } else {
                        if (!solSignMessage) throw new Error('Solana wallet not connected');
                        const encodedMessage = new TextEncoder().encode(message);
                        const signatureBytes = await solSignMessage(encodedMessage);
                        signature = bs58.encode(signatureBytes);
                    }

                    // 3. Login
                    const { token, expires_at } = await authLogin(address, chain, signature);

                    // 4. Cache in memory
                    tokenCache.set(cacheKey, { token, expires_at: expires_at * 1000, chain });

                    return token;
                } finally {
                    pendingRequests.current.delete(cacheKey);
                }
            })();

            pendingRequests.current.set(cacheKey, request);
            return request;
        },
        [evmSignMessage, solSignMessage]
    );

    /**
     * Clear cached tokens (on disconnect)
     */
    const clearTokens = useCallback(() => {
        tokenCache.clear();
    }, []);

    return { getAuthToken, clearTokens };
}
